import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import showToast from '../../utils/toast';
import AuthLayout from '../../components/auth/AuthLayout';
import authServices from '../../services/AuthServices';

const VerifyOtp = () => {
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const mobileFromState = location.state?.mobile;
  const storedMobile = localStorage.getItem('resetMobile');
  const mobile = mobileFromState || storedMobile || '';

  useEffect(() => {
    if (!mobile) {
      navigate('/forgot-password', { replace: true });
    }
  }, [mobile, navigate]);

  const handleChange = (e) => {
    const { value } = e.target;
    const formattedValue = value.replace(/\D/g, '');
    setOtp(formattedValue.slice(0, 6));
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (otp.length !== 6) {
      setError('Please enter the 6-digit OTP.');
      return;
    }

    if (!password || !confirmPassword) {
      setError('Please enter and confirm your new password.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authServices.resetPassword(mobile, otp, password, confirmPassword);

      if (response.success) {
        showToast.success(response.message || 'Password reset successfully.');

        // Clean up localStorage
        localStorage.removeItem('resetMobile');

        navigate('/login');
      } else {
        const message = response.message || 'Failed to reset password. Please try again.';
        setError(message);
        showToast.error(message);
      }
    } catch (error) {
      // Extract error message from error object
      const message = error.response?.data?.message ||
                     error.response?.data?.error ||
                     error.response?.data?.detail ||
                     error.message ||
                     'An error occurred. Please try again.';
      setError(message);
      showToast.error(message);
      console.error('Reset password error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/forgot-password');
  };

  return (
    <AuthLayout
      title="Reset Password"
      subtitle={`Enter the OTP sent to ${mobile ? `+91 ${mobile}` : 'your registered mobile number'} and set your new password.`}
      onBack={handleBack}
    >
      {error && (
        <div className="toast-error">
          <div className="toast-content">
            <div className="toast-icon">⚠️</div>
            <div className="toast-text">{error}</div>
            <button className="toast-close" onClick={() => setError('')}>
              ×
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">OTP</label>
          <input
            type="text"
            className="form-input"
            placeholder="Enter 6-digit OTP"
            value={otp}
            onChange={handleChange}
            maxLength={6}
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">New Password</label>
          <div className="password-input-container">
            <input
              type={showPassword ? "text" : "password"}
              className="form-input"
              placeholder="Enter new password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) {
                  setError('');
                }
              }}
              required
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
            </button>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Confirm Password</label>
          <div className="password-input-container">
            <input
              type={showConfirmPassword ? "text" : "password"}
              className="form-input"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (error) {
                  setError('');
                }
              }}
              required
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
            </button>
          </div>
        </div>

        <button type="submit" className="signin-btn" disabled={loading}>
          {loading ? 'Resetting password...' : 'Reset Password'}
        </button>
      </form>
    </AuthLayout>
  );
};

export default VerifyOtp;
